/* eslint-disable array-callback-return */
import React, { useState } from "react";
import { observer } from "mobx-react";
import { observable, action } from "mobx";
import DialerServiceData from "Stores/DialerServiceStore";
import userDetail from "Stores/userDetail";
import { transactionAnimationState } from "Components/common/TransactionAnimation";
import { toasterState } from "Components/common/toaster";
import { buyCreditsPopupState } from "./BuyCreditsPopup";

class VoBizBuyNumberPopupState {
  @observable showPopup = false;
  @observable callBack = () => {};

  @action
  setShowPopup = val => {
    this.showPopup = val;
    if (val) {
      document.getElementsByTagName("body")[0].classList.add("scrollHidden");
    } else {
      document.getElementsByTagName("body")[0].classList.remove("scrollHidden");
    }
  };

  @action
  setCallBack = val => {
    this.callBack = val;
  };
}

const voBizBuyNumberPopupState = new VoBizBuyNumberPopupState();

const NUMBER_SERIES = [
  { prefix: "80", label: "80 series", example: "+91 80XXX XXXXX", price: "$10/mo", cost: 10 },
  { prefix: "79", label: "79 series", example: "+91 79XXX XXXXX", price: "$12/mo", cost: 12 },
  { prefix: "92", label: "92 series", example: "+91 92XXX XXXXX", price: "$14/mo", cost: 14 }
];

// Step 1: Choose series
const SeriesStep = ({ selectedPrefix, setSelectedPrefix, onCancel, onNext, loadingNumbers, dialerCredits, onAddCredits }) => {
  const selectedSeries = NUMBER_SERIES.find(s => s.prefix === selectedPrefix);
  const hasEnoughCredits = dialerCredits >= (selectedSeries?.cost || 0);

  return (
    <>
      {/* Balance bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        padding: "8px 12px",
        marginBottom: "14px",
        fontSize: "13px"
      }}>
        <span style={{ color: "#6b7280" }}>Dialer Credits Balance</span>
        <span style={{ fontWeight: 700, color: hasEnoughCredits ? "#15803d" : "#dc2626" }}>
          ${parseFloat(dialerCredits || 0).toFixed(2)}
        </span>
      </div>

      <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }}>
        Choose a number series (monthly cost deducted from dialer credits):
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
        {NUMBER_SERIES.map(series => {
          const affordable = dialerCredits >= series.cost;
          return (
            <label
              key={series.prefix}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                border: `2px solid ${selectedPrefix === series.prefix ? "#3953fb" : "#e5e7eb"}`,
                borderRadius: "8px",
                cursor: affordable ? "pointer" : "not-allowed",
                backgroundColor: selectedPrefix === series.prefix ? "#f0f3ff" : affordable ? "#fff" : "#fafafa",
                opacity: affordable ? 1 : 0.6,
                transition: "all 0.15s"
              }}
              onClick={() => affordable && setSelectedPrefix(series.prefix)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="radio"
                  name="numberSeries"
                  value={series.prefix}
                  checked={selectedPrefix === series.prefix}
                  onChange={() => affordable && setSelectedPrefix(series.prefix)}
                  disabled={!affordable}
                  style={{ accentColor: "#3953fb", width: "16px", height: "16px", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                    {series.label}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>{series.example}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: selectedPrefix === series.prefix ? "#3953fb" : "#6b7280"
                }}>
                  {series.price}
                </span>
                {!affordable && (
                  <div style={{ fontSize: "11px", color: "#dc2626" }}>Insufficient credits</div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {!hasEnoughCredits && (
        <div style={{
          backgroundColor: "#fef2f2",
          border: "1px solid #fca5a5",
          borderRadius: "6px",
          padding: "8px 12px",
          fontSize: "12px",
          color: "#dc2626",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <span>Not enough credits for selected series.</span>
          <span
            style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
            onClick={onAddCredits}
          >
            Add Credits
          </span>
        </div>
      )}

      <div className="actionBtn">
        <div className="btnCont">
          <div className="backCont btn" onClick={onCancel}>Cancel</div>
          <div
            className={`purchaseBtn btn ${loadingNumbers || !hasEnoughCredits ? "disabled" : ""}`}
            onClick={!loadingNumbers && hasEnoughCredits ? onNext : undefined}
          >
            {loadingNumbers ? "Loading..." : "View Numbers"}
          </div>
        </div>
      </div>
    </>
  );
};

// Step 2b: Aadhaar verification for 92-series numbers
const AadhaarStep = ({ aadhaarNumber, setAadhaarNumber, aadhaarName, setAadhaarName, onBack, onNext, selectedNumber, seriesInfo }) => {
  const isValid = aadhaarNumber.replace(/\s/g, "").length === 12 && aadhaarName.trim().length > 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{
        backgroundColor: "#fffbeb",
        border: "1px solid #fcd34d",
        borderRadius: "6px",
        padding: "10px 12px",
        fontSize: "12px",
        color: "#78350f",
        display: "flex",
        gap: "8px",
        alignItems: "flex-start"
      }}>
        <i className="fa fa-exclamation-triangle" style={{ marginTop: "2px", color: "#d97706" }} />
        <span>92-series numbers require Aadhaar verification as per TRAI regulations. Your details will be submitted to our telephony carrier for compliance.</span>
      </div>

      <div style={{ fontSize: "13px", color: "#6b7280" }}>
        Number selected: <strong style={{ color: "#374151" }}>{selectedNumber}</strong>
      </div>

      <div>
        <label style={{ fontSize: "13px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
          Full Name (as on Aadhaar)
        </label>
        <input
          type="text"
          placeholder="Enter full name"
          value={aadhaarName}
          onChange={e => setAadhaarName(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "13px",
            outline: "none",
            boxSizing: "border-box"
          }}
        />
      </div>

      <div>
        <label style={{ fontSize: "13px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "6px" }}>
          Aadhaar Number
        </label>
        <input
          type="text"
          placeholder="XXXX XXXX XXXX"
          value={aadhaarNumber}
          maxLength={14}
          onChange={e => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
            const formatted = digits.replace(/(\d{4})(?=\d)/g, "$1 ");
            setAadhaarNumber(formatted);
          }}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "13px",
            outline: "none",
            letterSpacing: "1px",
            boxSizing: "border-box"
          }}
        />
        <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>12-digit Aadhaar UID number</div>
      </div>

      <div style={{ display: "flex", gap: "10px", paddingTop: "4px" }}>
        <div className="backCont btn" style={{ flex: 1 }} onClick={onBack}>← Back</div>
        <div
          className={`purchaseBtn btn ${!isValid ? "disabled" : ""}`}
          style={{ flex: 2 }}
          onClick={isValid ? onNext : undefined}
        >
          Continue to Purchase
        </div>
      </div>
    </div>
  );
};

// Step 2: Pick a number from the list
const NumberListStep = ({ numbers, selectedNumber, setSelectedNumber, onBack, onBuy, purchasing, seriesInfo, requiresAadhaar }) => (
  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
    {/* Sub-header */}
    <div style={{ marginBottom: "10px", flexShrink: 0 }}>
      <span style={{ fontSize: "13px", color: "#6b7280" }}>
        Available numbers for{" "}
        <strong style={{ color: "#374151" }}>{seriesInfo?.label}</strong>
        {" "}— {seriesInfo?.price}
      </span>
    </div>

    {/* Scrollable number list — takes remaining space */}
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        paddingRight: "4px",
        minHeight: 0
      }}
    >
      {numbers.length === 0 ? (
        <div style={{ textAlign: "center", color: "#9ca3af", fontSize: "13px", padding: "24px 0" }}>
          No numbers available for this series right now.
        </div>
      ) : (
        numbers.map((num, idx) => {
          const display = num.number || num;
          const isSelected = selectedNumber === display;
          return (
            <label
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                border: `2px solid ${isSelected ? "#3953fb" : "#e5e7eb"}`,
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: isSelected ? "#f0f3ff" : "#fff",
                transition: "all 0.15s",
                flexShrink: 0
              }}
              onClick={() => setSelectedNumber(display)}
            >
              <input
                type="radio"
                name="phoneNumber"
                value={display}
                checked={isSelected}
                onChange={() => setSelectedNumber(display)}
                style={{ accentColor: "#3953fb", width: "16px", height: "16px", flexShrink: 0 }}
              />
              <span style={{ fontSize: "14px", color: "#374151", fontWeight: isSelected ? 600 : 400 }}>
                {display}
              </span>
            </label>
          );
        })
      )}
    </div>

    {/* Action buttons — always at bottom */}
    <div style={{ flexShrink: 0, paddingTop: "16px", borderTop: "1px solid #f3f4f6", marginTop: "12px" }}>
      {selectedNumber && (
        <div style={{
          backgroundColor: "#f0f3ff",
          borderRadius: "6px",
          padding: "6px 10px",
          fontSize: "12px",
          color: "#374151",
          marginBottom: "10px",
          display: "flex",
          justifyContent: "space-between"
        }}>
          <span>Monthly cost for {selectedNumber}</span>
          <span style={{ fontWeight: 700, color: "#3953fb" }}>{seriesInfo?.price}</span>
        </div>
      )}
      <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
        <div className="backCont btn" style={{ flex: 1 }} onClick={onBack}>← Back</div>
        <div
          className={`purchaseBtn btn ${purchasing || !selectedNumber || numbers.length === 0 ? "disabled" : ""}`}
          style={{ flex: 2 }}
          onClick={!purchasing && selectedNumber && numbers.length > 0 ? onBuy : undefined}
        >
          {purchasing ? "Purchasing..." : requiresAadhaar ? "Next: Aadhaar Verify →" : `Buy for ${seriesInfo?.price || ""}`}
        </div>
      </div>
      <span className="chargeText">
        {seriesInfo?.cost} credits will be deducted from your dialer credits balance.
      </span>
    </div>
  </div>
);

const VoBizBuyNumberPopup = observer(() => {
  const [step, setStep] = useState(1);
  const [selectedPrefix, setSelectedPrefix] = useState("80");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [aadhaarName, setAadhaarName] = useState("");

  const teamId = userDetail?.userInfo?.team_id || userDetail?.userFeatureFlag?.id || "";
  const dialerCredits = parseFloat(userDetail?.userFeatureFlag?.dialerCredits || 0);
  const seriesInfo = NUMBER_SERIES.find(s => s.prefix === selectedPrefix);

  const handleAddCredits = () => {
    // Close this popup and open the credits popup
    voBizBuyNumberPopupState.setShowPopup(false);
    buyCreditsPopupState.setNewPayment(false);
    buyCreditsPopupState.setCallBack(() => {
      // After credits are added, re-open the buy number popup
      voBizBuyNumberPopupState.setShowPopup(true);
    });
    buyCreditsPopupState.setPopupValue({ type: "DIALER" });
    buyCreditsPopupState.setShowPopup(true);
  };

  const handleViewNumbers = async () => {
    setLoadingNumbers(true);
    try {
      const results = await DialerServiceData.getAvailableIndiaNumbers(selectedPrefix);
      setAvailableNumbers(results || []);
      setSelectedNumber(results?.[0]?.number || results?.[0] || "");
      setStep(2);
    } catch (e) {
      toasterState.setToastMsg("Failed to load available numbers. Please try again.", "failure");
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else {
      setStep(1);
      setAvailableNumbers([]);
      setSelectedNumber("");
    }
  };

  const handleNumberSelected = () => {
    if (selectedPrefix === "92") {
      setStep(3); // Aadhaar verification required for 92-series
    } else {
      handlePurchase();
    }
  };

  const handlePurchase = async () => {
    if (!selectedNumber || !teamId) return;
    const cost = seriesInfo?.cost || 0;
    const aadhaar = selectedPrefix === "92" ? { aadhaarNumber: aadhaarNumber.replace(/\s/g, ""), aadhaarName } : {};
    setPurchasing(true);
    transactionAnimationState.setShowPopup(true);
    try {
      const result = await DialerServiceData.purchaseIndiaNumber(selectedNumber, cost, aadhaar);
      if (result?.success) {
        closePopup();
        voBizBuyNumberPopupState.callBack && voBizBuyNumberPopupState.callBack(selectedNumber);
      } else if (result?.creditsInsufficient) {
        toasterState.setToastMsg(
          `Insufficient credits. Need $${result.required?.toFixed(2)}, have $${result.available?.toFixed(2)}. Please add credits.`,
          "failure"
        );
      }
    } catch (e) {
      toasterState.setToastMsg("Failed to purchase number. Please try again.", "failure");
    } finally {
      transactionAnimationState.setShowPopup(false);
      setPurchasing(false);
    }
  };

  const closePopup = () => {
    setStep(1);
    setSelectedPrefix("80");
    setAvailableNumbers([]);
    setSelectedNumber("");
    setPurchasing(false);
    setLoadingNumbers(false);
    setAadhaarNumber("");
    setAadhaarName("");
    voBizBuyNumberPopupState.setShowPopup(false);
  };

  if (!voBizBuyNumberPopupState.showPopup) return null;

  return (
    <div className="buyNewNumberPopup popupWrapper">
      <div className="confirmation-popup common-popup">
        <div className="newNumberPopupWrapper popup-wrap animated customzoomIn">
          <div className="newNumberCont">
            <div className="popupHeader">
              <div className="popupTitle">Buy India Phone Number</div>
              <div className="popup-close" title="Close" onClick={closePopup}>
                <i className="material-icons">close</i>
              </div>
            </div>
            <div className="popupBody" style={{ display: "flex", flexDirection: "column", height: step !== 1 ? "360px" : "auto", overflow: "hidden" }}>
              {step === 1 ? (
                <SeriesStep
                  selectedPrefix={selectedPrefix}
                  setSelectedPrefix={setSelectedPrefix}
                  onCancel={closePopup}
                  onNext={handleViewNumbers}
                  loadingNumbers={loadingNumbers}
                  dialerCredits={dialerCredits}
                  onAddCredits={handleAddCredits}
                />
              ) : step === 2 ? (
                <NumberListStep
                  numbers={availableNumbers}
                  selectedNumber={selectedNumber}
                  setSelectedNumber={setSelectedNumber}
                  onBack={handleBack}
                  onBuy={handleNumberSelected}
                  purchasing={purchasing}
                  seriesInfo={seriesInfo}
                  requiresAadhaar={selectedPrefix === "92"}
                />
              ) : (
                <AadhaarStep
                  aadhaarNumber={aadhaarNumber}
                  setAadhaarNumber={setAadhaarNumber}
                  aadhaarName={aadhaarName}
                  setAadhaarName={setAadhaarName}
                  onBack={handleBack}
                  onNext={handlePurchase}
                  selectedNumber={selectedNumber}
                  seriesInfo={seriesInfo}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="common-popup-mask" />
    </div>
  );
});

export default VoBizBuyNumberPopup;
export { VoBizBuyNumberPopup, voBizBuyNumberPopupState };
